import type { CSSObject, DataAttributeMap } from '@balance-web/core';
import { buildDataAttributes } from '@balance-web/core';
import {
  FOCUS_RING,
  TEXT_LINK_VISITED,
  useRawTheme,
  useTheme,
} from '@balance-web/theme';
import { hexToRgb } from '@balance-web/utils';

export type UseTextLinkProps = {
  color?: 'base' | 'link';
  data?: DataAttributeMap;
  id?: string;
  /** does not support css */
  css?: never;
  /** does not support style */
  style?: never;
  /** hover state from parent */
  hover?: boolean;
};

export function useTextLink({
  color = 'link',
  data,
  id,
  hover,
}: UseTextLinkProps) {
  const { spacing, typography } = useTheme();
  const rawTheme = useRawTheme();
  // A hex literal, not a CSS custom property: `hexToRgb` below cannot parse one.
  const resolvedColor =
    color === 'link'
      ? rawTheme.semantic.colour.text.textLink
      : rawTheme.semantic.colour.text.textPrimary;

  const testingProps = {
    id,
    ...(data ? buildDataAttributes(data) : undefined),
  };

  const styles: CSSObject = {
    color: resolvedColor,
    cursor: 'pointer',
    fontWeight: typography.fontWeight.medium,
    textDecoration: 'underline',
    // FIXME: avoid colour mutation outside the theme
    textDecorationColor: hover ? resolvedColor : hexToRgb(resolvedColor, 0.32),

    // cover all cases by desaturating and lowering opacity
    '&[aria-disabled=true]': {
      cursor: 'default',
      filter: 'grayscale(100%)',
      mixBlendMode: 'luminosity', // pick up the background hue to avoid harsh neutrals
      opacity: 0.6,
    },

    '&:not([aria-disabled=true])': {
      ':hover, :focus': {
        textDecorationColor: resolvedColor,
      },

      '&[target=_blank]:visited': {
        color: TEXT_LINK_VISITED,
        textDecorationColor: TEXT_LINK_VISITED,
        svg: {
          stroke: TEXT_LINK_VISITED,
        },
      },
    },

    '&:not([aria-disabled=true]):focus-visible': {
      outline: `2px solid ${FOCUS_RING}`,
      outlineOffset: 2,
    },

    // The svg dims are hard coded because we don't have icon sizing tokens.
    svg: {
      paddingLeft: spacing.xsmall,
      marginBottom: 2,
    },
    '&[data-textsize=small],&[data-textsize=xsmall]': {
      svg: {
        width: 12,
        height: 12,
      },
    },
    '&[data-textsize=medium]': {
      svg: {
        width: 14,
        height: 14,
      },
    },
    '&[data-textsize=large],&[data-textsize=xlarge]': {
      svg: {
        width: 16,
        height: 16,
      },
    },
  };

  return { testingProps, styles };
}
